import type { LeasingDocs } from '@/models/docs';
import { Trash2 } from 'lucide-react';

import { formatDate } from '@/lib/utils';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DocTypeBadge } from '@/components/DocTypeBadge';
import { Skeleton } from '@/components/ui/skeleton';


import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip';

interface DocumentsTableProps {
    documents: LeasingDocs[];
    onSelectDocument: (document: LeasingDocs) => void;
    selectedDocumentId?: number;
    onDeleteDocument?: (document: LeasingDocs) => void;
    loading?: boolean;
}

export function DocumentsTable({
    documents,
    onSelectDocument,
    selectedDocumentId,
    onDeleteDocument,
    loading = false,
}: DocumentsTableProps) {
    return (
        <div className="border rounded-md">
            <Table className="text-sm">
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-1/4">Document</TableHead>
                        <TableHead className="w-2/5">Details</TableHead>
                        <TableHead>Aircraft & Authority</TableHead>
                        <TableHead className="w-16"></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading && (
                        Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}>
                                <TableCell className="py-2">
                                    <div className="flex flex-col">
                                        <Skeleton className="w-24 h-6 mb-1" />
                                        <Skeleton className="w-16 h-4 ml-1" />
                                    </div>
                                </TableCell>
                                <TableCell className="py-2">
                                    <div className="flex flex-col">
                                        <Skeleton className="w-64 h-6 mb-1" />
                                        <Skeleton className="w-32 h-4" />
                                    </div>
                                </TableCell>
                                <TableCell className="py-2">
                                    <div className="flex flex-col text-sm">
                                        <Skeleton className="w-32 h-6 mb-1" />
                                        <Skeleton className="w-24 h-4" />
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Skeleton className="h-6 w-8 rounded-full" />
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                    {!loading && documents.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={3}
                                className="text-center py-8 text-gray-500"
                            >
                                No documents found
                            </TableCell>
                        </TableRow>
                    )}
                    {!loading && documents.length > 0 && (
                        documents.map((document) => (
                            <TableRow
                                key={document.id}
                                className={`cursor-pointer hover:bg-gray-50 ${
                                    selectedDocumentId === document.id
                                        ? 'bg-blue-50'
                                        : ''
                                }`}
                                onClick={() => onSelectDocument(document)}
                            >
                                <TableCell className="py-2">
                                    <div className="flex flex-col">
                                        {document?.meta?.raw?.type ? (
                                            <DocTypeBadge
                                                type={document.meta.raw.type}
                                                className="w-fit font-normal mb-1"
                                            />
                                        ) : (
                                            <Badge className="w-fit font-normal text-xs mb-1">
                                                Processing
                                            </Badge>
                                        )}
                                        <div className="text-xs text-gray-500 ml-1">
                                            {formatDate(
                                                document?.meta?.raw?.issueDate,
                                            )}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="py-2">
                                    <div className="flex flex-col">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="font-medium truncate w-64">
                                                    {document?.meta?.raw
                                                        ?.title || 'Untitled'}
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {document?.meta?.raw?.title ||
                                                    'Untitled'}
                                            </TooltipContent>
                                            <div className="text-xs">
                                                {document?.meta?.raw?.id}
                                            </div>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                                <TableCell className="py-2">
                                    <div className="flex flex-col text-sm">
                                        <div>
                                            {document?.meta?.raw?.aircraft
                                                ?.type || '-'}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {document?.meta?.raw
                                                ?.issuingAuthority || '-'}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="ghost"
                                        onClick={() =>
                                            onDeleteDocument &&
                                            onDeleteDocument(document)
                                        }
                                    >
                                        <Trash2 className="h-5 w-5 text-amber-600 hover:scale-105 transition-all" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
}
